import { useMutation, useQuery } from '@apollo/client'
import {
  Container,
  Divider,
  ListItem,
  ListItemText,
  Paper,
  Stack,
} from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AddResourcesDocument,
  CustomPaymentPluginDocument,
  PluginCustomPayment,
  PluginId,
  UpdateCustomPaymentPluginDocument,
} from '../../../../generated/graphql'
import {
  AuthenticationDialog,
  AuthenticationDialogForm,
} from '../authentication/dialog'
import { AuthenticationSection } from '../authentication/section'
import { SectionHeader, UploadedFile } from '@sitoo/mui-components'
import { FormProvider, useForm } from 'react-hook-form'
import { PluginsForm } from '../plugins-form'
import { DropzoneInput } from '../../../../inputs/dropzone-input'
import { TextFieldInput } from '../../../../inputs/text-field-input'
import { SwitchInput } from '../../../../inputs/switch-input'

type Form = PluginCustomPayment & {
  payment_file_large: UploadedFile | undefined | null
  payment_file_small: UploadedFile | undefined | null
}

const isValidHexColor = (value: unknown): boolean => {
  if (typeof value === 'string') {
    const shortHexColor = /^#([0-9A-Fa-f]{3})$/
    const longHexColor = /^#([0-9A-Fa-f]{6})$/

    return shortHexColor.test(value) || longHexColor.test(value)
  }
  return false
}

export const SettingsCustomPaymentPage = () => {
  const { t } = useTranslation(['shared', 'apps'])

  const formContext = useForm<Form>({
    defaultValues: {
      api_url: '',
      api_key: '',
      bg_payment: '',
      bg_payment_small: '',
      payment_file_large: undefined,
      payment_file_small: undefined,
    },
  })

  const { control, formState, watch } = formContext

  const { data, loading: fetchLoading } = useQuery(
    CustomPaymentPluginDocument,
    { fetchPolicy: 'cache-and-network' },
  )
  const plugin = data?.customPaymentPlugin

  const [isAuthOpen, setAuthOpen] = useState(false)
  const isAuthConfigured = Boolean(plugin?.api_url && plugin?.api_key)

  const [updateSettings, { loading: updateLoading }] = useMutation(
    UpdateCustomPaymentPluginDocument,
  )

  const onAuthSubmit = async (formData: AuthenticationDialogForm) => {
    await updateSettings({
      variables: {
        data: {
          id: PluginId.Custompayment,
          api_url: formData.login,
          api_key: formData.password,
        },
      },
    })
  }

  const onSubmit = async (formData: Form) => {
    const {
      payment_file_large,
      payment_file_small,
      icon_payment_resource,
      icon_payment_small_resource,
      ...submitData
    } = formData
    let icon_payment = undefined
    let icon_payment_small = undefined

    if (payment_file_large !== undefined) {
      const resourceId = payment_file_large
        ? await uploadFileAndGetResourceId(payment_file_large)
        : null

      icon_payment = resourceId
    }

    if (payment_file_small !== undefined) {
      const resourceId = payment_file_small
        ? await uploadFileAndGetResourceId(payment_file_small)
        : null

      icon_payment_small = resourceId
    }

    const { data } = await updateSettings({
      variables: { data: { ...submitData, icon_payment, icon_payment_small } },
    })
    return data?.updateCustomPayment
  }

  const [uploadFile, { loading: uploadLoading }] = useMutation(
    AddResourcesDocument,
    { context: { headers: { 'apollo-require-preflight': true } } },
  )

  const isLoading = fetchLoading || uploadLoading || updateLoading

  const uploadFileAndGetResourceId = async (file: UploadedFile) => {
    const { data } = await uploadFile({
      variables: {
        resources: [file.file],
      },
    })

    return data?.addResources?.[0]?.return
  }

  return (
    <Container>
      <AuthenticationDialog
        open={isAuthOpen}
        onClose={() => setAuthOpen(false)}
        onSubmit={onAuthSubmit}
        defaultValues={{
          login: data?.customPaymentPlugin.api_url,
        }}
        slotProps={{
          loginInput: {
            label: t('apps:custom_payment.username_label'),
            helperText: t('apps:custom_payment.username_helper'),
            rules: {
              startsWithHttps: (value) => {
                if (value && !value.startsWith('https://'))
                  return t('apps:custom_payment.url_https_error')
              },
              endsWithSlash: (value) => {
                if (value && !value.endsWith('/'))
                  return t('apps:custom_payment.url_trailing_slash_error')
              },
            },
          },
          passwordInput: {
            label: t('apps:custom_payment.password_label'),
            helperText: t('apps:custom_payment.password_helper'),
          },
        }}
      />

      <AuthenticationSection
        isConfigured={isAuthConfigured}
        onClick={() => setAuthOpen(true)}
        isLoading={isLoading}
        disabled={formState.isDirty}
      />

      <FormProvider {...formContext}>
        <PluginsForm
          serverData={plugin}
          onSubmit={onSubmit}
          submitLabel={t('shared:action.save')}
          isLoading={isLoading}
        >
          <Paper sx={{ my: 2 }}>
            <SectionHeader variant="transparent">
              {t('apps:custom_payment.settings_label')}
            </SectionHeader>

            <Stack gap={2} sx={{ p: 2, pt: 0 }}>
              <DropzoneInput
                name="payment_file_large"
                control={control}
                multiple={false}
                resource={plugin?.icon_payment_resource}
                isLoading={isLoading}
                label={t('apps:custom_payment:payment_file_large_label')}
                maxSize={7 * 1024 * 1024}
                accept={{ 'image/png': ['.png'] }}
                helperText={t('apps:custom_payment:payment_file_large_helper')}
                backgroundColor={watch('bg_payment') ?? undefined}
                sx={{ '.DropzoneContainer': { gridTemplateColumns: 'none' } }}
              />

              <TextFieldInput
                name="bg_payment"
                control={control}
                label={t('apps:custom_payment:payment_color_large_label')}
                helperText={t('apps:custom_payment:payment_color_large_helper')}
                required
                rules={{
                  validate: {
                    hexColor: (value) => {
                      if (!isValidHexColor(value)) {
                        return t('apps:custom_payment:hex_color_error')
                      }
                    },
                  },
                }}
              />
            </Stack>

            <Divider />

            <Stack gap={2} sx={{ p: 2 }}>
              <DropzoneInput
                name="payment_file_small"
                multiple={false}
                control={control}
                resource={plugin?.icon_payment_small_resource}
                maxSize={7 * 1024 * 1024}
                accept={{ 'image/png': ['.png'] }}
                isLoading={isLoading}
                label={t('apps:custom_payment:payment_file_small_label')}
                helperText={t('apps:custom_payment:payment_file_small_helper')}
                backgroundColor={watch('bg_payment_small') ?? undefined}
                sx={{ '.DropzoneContainer': { gridTemplateColumns: 'none' } }}
              />

              <TextFieldInput
                name="bg_payment_small"
                control={control}
                label={t('apps:custom_payment:payment_color_small_label')}
                helperText={t('apps:custom_payment:payment_color_small_helper')}
                required
                rules={{
                  validate: {
                    hexColor: (value) => {
                      if (!isValidHexColor(value)) {
                        return t('apps:custom_payment:hex_color_error')
                      }
                    },
                  },
                }}
              />
            </Stack>

            <Divider />

            <ListItem
              component="div"
              divider
              secondaryAction={
                <SwitchInput
                  name="require_phone_number"
                  control={control}
                  aria-label={t(
                    'apps:custom_payment.require_phone_number_label',
                  )}
                />
              }
            >
              <ListItemText
                primary={t('apps:custom_payment.require_phone_number_label')}
                secondary={t('apps:custom_payment.require_phone_number_helper')}
              />
            </ListItem>

            <ListItem
              component="div"
              divider
              secondaryAction={
                <SwitchInput
                  name="can_be_combined"
                  control={control}
                  aria-label={t('apps:custom_payment.combine_payments_label')}
                />
              }
            >
              <ListItemText
                primary={t('apps:custom_payment.combine_payments_label')}
                secondary={t('apps:custom_payment.combine_payments_helper')}
              />
            </ListItem>
          </Paper>
        </PluginsForm>
      </FormProvider>
    </Container>
  )
}
